import { NgModule, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MyApp } from 'app/app.component';
import { AuthService } from 'services/auth.service';
import { LocationService } from 'services/location.service';
import { SettingsService } from 'services/settings.service';
import { NotificationService } from 'services/notification.service';

import { FoodDbService } from 'services/food-db.service';
import { ComponentsModule } from 'components/components.module';
import { DirectivesModule } from 'directives/directives.module';
import { ExerciseDbService } from 'services/exercise-db.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { UserDb } from 'services/user-db.service';
import { DayTaskStatusService } from 'services/day-task-status.service';
import { QuickProfileService } from 'services/quick-profile.service';
import { UnsavedChangesGuardProvider } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { FlagContentService } from 'services/flag-content.service';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { isLocalEnv } from 'environments/environment-helpers';
import { ImageService } from 'services/image-service';
import { DebugService } from 'services/debug.service';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { TestCasesDbService } from 'services/tests-cases-db.service';
import { OverlayStackService } from 'providers/overlay-stack.service';
import { AlertController } from 'providers/alert-controller';
import { ActivityMenuService } from 'services/activity-menu.service';
import { MealActionsService } from 'services/meal-actions.service';
import { WorkoutStateService } from 'services/workout-state.service';
import { ToastController } from 'providers/toast-controller';
import { DatabaseWrapperProvider } from 'services/database-wrapper';
import { FirebaseService } from 'services/firebase.service';
import { NavController } from 'providers/nav-controller';
import { TextDbService } from 'services/text-db.service';
import { LoggingService } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { RECOVERY_REPORTING_SERVICE } from 'services/recovery-reporting-service';
import { WakeLockService } from 'services/wake-lock.service';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { SsoService } from 'services/sso.service';
import { TutorialController } from 'providers/tutorial-controller';
import { AnimationController } from 'providers/animation-controller';
import { ConfigDbService } from 'services/config-db.service';
import { CommonDbService } from 'services/common-db.service';
import { StapleDbService } from 'services/staple-db.service';
import { UrlQueryService } from 'services/url-query-service';
import { BackdropController } from 'providers/backdrop-controller';
import { PayProPriceFetcher as PayProPriceFetcherService } from 'services/paypro-price-fetcher.service';
import { IAPService } from 'services/iap.service';
import { SubscriptionManager } from 'services/subscription-manager.service';
import { PopoverController } from 'providers/popover-controller';
import { VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { TelemetryService } from 'services/telemetry.service';
import { ReviewPromptService } from 'services/review-prompt.service';
import { PubSubService } from 'services/pubsub.service';
import { LoadingController } from 'providers/loading-controller';
import { MenuController } from 'providers/menu-controller';
import { UrlService } from 'services/url.service';
import { NavigationBreadcrumbService } from '../services/navigation-breadcrumb.service';
import { ExternalFlowService } from 'services/external-flow.service';
import { AppService } from 'services/app.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LocationStrategy, Location, PathLocationStrategy } from '@angular/common';
import { SentryErrorHandler } from './app-sentry';
import { AdminPagesModule } from 'app/features/admin-pages.module';
import { CoachPagesModule } from 'app/features/coach-pages.module';
import { CorePagesModule } from 'app/features/core-pages.module';
import { NutritionPagesModule } from 'app/features/nutrition-pages.module';
import { OnboardingPagesModule } from 'app/features/onboarding-pages.module';
import { ScannerPagesModule } from 'app/features/scanner-pages.module';
import { SettingsPagesModule } from 'app/features/settings-pages.module';
import { SubscriptionPagesModule } from 'app/features/subscription-pages.module';
import { TestingPagesModule } from 'app/features/testing-pages.module';
import { TrainingPagesModule } from 'app/features/training-pages.module';

class DummyHandler {
};

@NgModule({ declarations: [MyApp],
    bootstrap: [MyApp], imports: [
        //RouterModule.forRoot([]),
        BrowserModule,
        BrowserAnimationsModule,
        DragDropModule,
        ComponentsModule,
        DirectivesModule,
        // Every page in the app is registered through one of these, grouped by
        // product area. Do NOT add a page module here: put it in the area
        // module it belongs to, under app/features/. This list used to hold all
        // 91 of them by hand, which nobody could read and every branch had to
        // edit. `scripts/ci/check-app-module-page-registration.mjs` keeps it
        // that way.
        AdminPagesModule,
        CoachPagesModule,
        CorePagesModule,
        NutritionPagesModule,
        OnboardingPagesModule,
        ScannerPagesModule,
        SettingsPagesModule,
        SubscriptionPagesModule,
        TestingPagesModule,
        TrainingPagesModule,
    ], providers: [
        provideZonelessChangeDetection(),
        Location,
        FoodDbService,
        ExerciseDbService,
        TestCasesDbService,
        AuthService,
        AppService,
        DatabaseWrapperProvider,
        LocationService,
        LoggingService,
        DatabaseRecoveryService,
        // How database recovery reaches the reporting service. The two call each other, so
        // neither can take the other through its constructor; recovery asks for this token at
        // the moment it reports something instead. This module is the one place that knows both
        // halves — see `recovery-reporting-service.ts`.
        { provide: RECOVERY_REPORTING_SERVICE, useExisting: LoggingService },
        PayProPriceFetcherService,
        ConfigDbService,
        CommonDbService,
        StapleDbService,
        ActivityMenuService,
        MealActionsService,
        WorkoutStateService,
        CloudFunctionsService,
        FlagContentService,
        QuickProfileService,
        DebugService,
        PubSubService,
        SettingsService,
        NotificationService,
        UrlService,
        NavigationBreadcrumbService,
        UserDb,
        DayTaskStatusService,
        SvPlanHandlerService,
        ComponentsModule,
        DirectivesModule,
        UnsavedChangesGuardProvider,
        PopupMenuProvider,
        OverlayStackService,
        AlertController,
        ToastController,
        PopoverController,
        LoadingController,
        {
          provide: WakeLockService,
          // factory keeps the @capacitor-community/keep-awake import out of
          // wake-lock.service.ts itself, so the service is testable without
          // ESM module-loader complications. No platform gate here: the plugin
          // dispatches to its own per-platform impl (iOS / Android natively,
          // web via navigator.wakeLock; rejects on older browsers without the
          // Screen Wake Lock API, which WakeLockService's catch absorbs and
          // logs). Gating here previously made the plugin a hard no-op on PWA,
          // which silently lost the wake-lock coverage that intake-form's
          // unconditional pre-refactor `KeepAwake.keepAwake()` provided.
          useFactory: () => new WakeLockService(
            () => KeepAwake.keepAwake(),
            () => KeepAwake.allowSleep(),
          ),
        },
        MenuController,
        BackdropController,
        TutorialController,
        AnimationController,
        NavController,
        ImageService,
        UrlQueryService,
        TelemetryService,
        ReviewPromptService,
        FirebaseService,
        TextDbService,
        SsoService,
        VirtualKeyboardService,
        ExternalFlowService,
        IAPService,
        SubscriptionManager,
        // install the error handler, only if not local development
        { provide: !isLocalEnv() ? ErrorHandler : DummyHandler, useClass: SentryErrorHandler },
        // make sure we DON'T have # in the urls
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule { }